export interface ReportCustomerItemOrderForecast {
  predicted_quantity?: number;
  last_order_quantity?: number;
  days_until_order?: number;
  days_since_last_order?: number;
  median_cadence_days?: number;
  history_order_count?: number;
}

export interface ReportItemOrderForecastSummary {
  next_7_days_quantity?: number;
  total_predicted_quantity?: number;
  overdue_quantity?: number;
  customers_due_7d_count?: number;
  customers_due_count?: number;
  insufficient_history_customers?: number;
  churned_customers_count?: number;
  customer_forecasts?: ReportCustomerItemOrderForecast[];
  churned_customers?: ReportCustomerItemOrderForecast[];
}

type NormalizedForecast<T extends ReportCustomerItemOrderForecast> = T & {
  predicted_quantity: number;
  last_order_quantity: number;
  days_until_order: number;
  days_since_last_order: number;
  median_cadence_days: number;
  history_order_count: number;
};

type NormalizedForecastSummary<T extends ReportItemOrderForecastSummary> = Omit<
  T,
  | 'total_predicted_quantity'
  | 'next_7_days_quantity'
  | 'overdue_quantity'
  | 'customers_due_7d_count'
  | 'customers_due_count'
  | 'insufficient_history_customers'
  | 'churned_customers_count'
  | 'customer_forecasts'
  | 'churned_customers'
> & {
  total_predicted_quantity: number;
  next_7_days_quantity: number;
  overdue_quantity: number;
  customers_due_7d_count: number;
  customers_due_count: number;
  insufficient_history_customers: number;
  churned_customers_count: number;
  customer_forecasts: NormalizedForecast<NonNullable<T['customer_forecasts']>[number]>[];
  churned_customers: NormalizedForecast<NonNullable<T['churned_customers']>[number]>[];
};

function normalizeCustomerForecast<T extends ReportCustomerItemOrderForecast>(
  forecast: T,
): NormalizedForecast<T> {
  return {
    ...forecast,
    predicted_quantity: forecast.predicted_quantity ?? 0,
    last_order_quantity: forecast.last_order_quantity ?? 0,
    days_until_order: forecast.days_until_order ?? 0,
    days_since_last_order: forecast.days_since_last_order ?? 0,
    median_cadence_days: forecast.median_cadence_days ?? 0,
    history_order_count: forecast.history_order_count ?? 0,
  };
}

export function normalizeItemOrderForecastSummaries<T extends ReportItemOrderForecastSummary>(
  forecasts: T[],
): NormalizedForecastSummary<T>[] {
  return forecasts.map((item) => {
    const customerForecasts = Array.isArray(item.customer_forecasts)
      ? item.customer_forecasts.map(normalizeCustomerForecast)
      : [];
    const churnedCustomers = Array.isArray(item.churned_customers)
      ? item.churned_customers.map(normalizeCustomerForecast)
      : [];
    return {
      ...item,
      total_predicted_quantity: item.total_predicted_quantity ?? 0,
      next_7_days_quantity: item.next_7_days_quantity ?? item.overdue_quantity ?? 0,
      overdue_quantity: item.overdue_quantity ?? 0,
      customers_due_7d_count: item.customers_due_7d_count ?? customerForecasts.filter((forecast) => forecast.days_until_order <= 7).length,
      customers_due_count: item.customers_due_count ?? customerForecasts.length,
      insufficient_history_customers: item.insufficient_history_customers ?? 0,
      churned_customers_count: item.churned_customers_count ?? churnedCustomers.length,
      customer_forecasts: customerForecasts,
      churned_customers: churnedCustomers,
    };
  });
}
